import { ButtonPreset } from '@/constants/componentPresets';
import { ColorType } from '@/theme/colors';
import type { FontWeight } from '@/theme/typography';
import type { ButtonPresetConfig } from './buttonPresetConfigs';

interface ResolveButtonVisualStateInput {
  config: ButtonPresetConfig;
  preset: ButtonPreset;
  isHovered: boolean;
  isPressed: boolean;
  disableHoverEffects: boolean;
  textWeight?: FontWeight;
}

interface ResolveStateNumberInput {
  isHovered: boolean;
  isPressed: boolean;
  baseValue: number;
  hoverValue?: number;
  pressValue?: number;
}

export interface ButtonVisualState {
  isHovered: boolean;
  isPressed: boolean;
  glossy: number;
  backgroundOpacity: number;
  borderOpacity: number;
  glow: number;
  backdropBlur: number;
  glowColor: ColorType;
  textWeight: FontWeight;
  shadow: string | undefined;
}

function resolveStateNumber({
  isHovered,
  isPressed,
  baseValue,
  hoverValue,
  pressValue,
}: ResolveStateNumberInput): number {
  if (isPressed && pressValue != null) {
    return pressValue;
  }

  if (isHovered && hoverValue != null) {
    return hoverValue;
  }

  return baseValue;
}

function resolveGlossy({
  config,
  preset,
  isHovered,
}: Pick<ResolveButtonVisualStateInput, 'config' | 'preset' | 'isHovered'>): number {
  if (config.glossy != null) {
    return config.glossy;
  }

  if (preset === ButtonPreset.Ghost) {
    return 0;
  }

  return isHovered ? 2 : 1;
}

function resolveTextWeight({
  config,
  isHovered,
  isPressed,
  textWeight,
}: Pick<ResolveButtonVisualStateInput, 'config' | 'isHovered' | 'isPressed' | 'textWeight'>): FontWeight {
  if (textWeight != null) {
    return textWeight;
  }

  if ((isPressed || isHovered) && config.hoverTextWeight != null) {
    return config.hoverTextWeight;
  }

  return config.textWeight;
}

function resolveShadow({
  config,
  isHovered,
  isPressed,
}: Pick<ResolveButtonVisualStateInput, 'config' | 'isHovered' | 'isPressed'>): string | undefined {
  if (isPressed && config.clickShadow) {
    return config.clickShadow;
  }

  if (!config.customShadow) {
    return undefined;
  }

  if (isPressed) {
    return config.customShadow.click;
  }

  if (isHovered) {
    return config.customShadow.hover;
  }

  return config.customShadow.regular;
}

export function resolveButtonVisualState({
  config,
  preset,
  isHovered,
  isPressed,
  disableHoverEffects,
  textWeight,
}: ResolveButtonVisualStateInput): ButtonVisualState {
  const effectiveIsHovered = disableHoverEffects ? false : isHovered;
  const effectiveIsPressed = disableHoverEffects ? false : isPressed;

  return {
    isHovered: effectiveIsHovered,
    isPressed: effectiveIsPressed,
    glossy: resolveGlossy({ config, preset, isHovered: effectiveIsHovered }),
    backgroundOpacity: resolveStateNumber({
      isHovered: effectiveIsHovered,
      isPressed: effectiveIsPressed,
      baseValue: config.bgOpacity,
      hoverValue: config.hoverBgOpacity,
      pressValue: config.clickBgOpacity,
    }),
    borderOpacity: resolveStateNumber({
      isHovered: effectiveIsHovered,
      isPressed: effectiveIsPressed,
      baseValue: config.borderOpacity,
      hoverValue: config.hoverBorderOpacity,
      pressValue: config.clickBorderOpacity,
    }),
    glow: resolveStateNumber({
      isHovered: effectiveIsHovered,
      isPressed: effectiveIsPressed,
      baseValue: config.glow,
      hoverValue: config.hoverGlow,
      pressValue: config.clickGlow,
    }),
    backdropBlur: preset === ButtonPreset.Ghost && effectiveIsPressed ? 2 : config.backdropBlur,
    glowColor:
      preset === ButtonPreset.Secondary || preset === ButtonPreset.Secondary2
        ? ColorType.White
        : ColorType.Accent,
    textWeight: resolveTextWeight({
      config,
      isHovered: effectiveIsHovered,
      isPressed: effectiveIsPressed,
      textWeight,
    }),
    shadow: resolveShadow({
      config,
      isHovered: effectiveIsHovered,
      isPressed: effectiveIsPressed,
    }),
  };
}
